import { useEffect, useRef, useState, useCallback } from "react";

const WS_BASE = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws") + "/api/ws";

export function useCollaboration({ diagramId, enabled, token, onRemoteCodeChange, onRemoteAnnotationsChange }) {
  const wsRef = useRef(null);
  const [peers, setPeers] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [connected, setConnected] = useState(false);
  const [permissions, setPermissions] = useState({ can_edit_notes: true, is_owner: false });
  const reconnectTimer = useRef(null);

  const connect = useCallback(() => {
    if (!diagramId || !enabled) return;
    const url = `${WS_BASE}/${diagramId}${token ? `?token=${token}` : ""}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === "init") {
          setSessionId(msg.session_id);
          setPeers(msg.peers || []);
          if (msg.permissions) setPermissions(msg.permissions);
        } else if (msg.type === "user_joined") {
          setPeers(msg.peers || []);
        } else if (msg.type === "user_left") {
          setPeers(prev => prev.filter(p => p.session_id !== msg.session_id));
        } else if (msg.type === "code_change") {
          onRemoteCodeChange?.(msg.code, msg.session_id);
        } else if (msg.type === "annotations_change") {
          onRemoteAnnotationsChange?.(msg.annotations, msg.node_colors, msg.session_id);
        } else if (msg.type === "permission_changed") {
          setPermissions(prev => ({ ...prev, can_edit_notes: msg.collab_can_edit_notes }));
        }
      } catch {}
    };

    ws.onclose = () => {
      setConnected(false);
      reconnectTimer.current = setTimeout(() => connect(), 3000);
    };

    ws.onerror = () => ws.close();
  }, [diagramId, enabled, token, onRemoteCodeChange, onRemoteAnnotationsChange]);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [connect]);

  const sendCodeChange = useCallback((code) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "code_change", code }));
    }
  }, []);

  const sendAnnotationsChange = useCallback((annotations, node_colors) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "annotations_change", annotations, node_colors }));
    }
  }, []);

  const sendPermissionUpdate = useCallback((collab_can_edit_notes) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "permission_update", collab_can_edit_notes }));
    }
  }, []);

  return { peers, sessionId, connected, permissions, sendCodeChange, sendAnnotationsChange, sendPermissionUpdate };
}
